/**
 * [PM-SG 2026-08-12] RACE CONTROL — one announcement, centred.
 *
 * THE SHAPE. An announcement is a single fact, said once, in the middle of the screen. The
 * version before this put it in a card pinned to the left edge, which left a hole where the eye
 * goes first and made a celebration read like a form. Everything now sits on the centre line and
 * is read top to bottom: what kind of thing happened, who it happened to, how much, and where
 * that leaves them. No card, no border, no columns — the screen IS the card.
 *
 * WHAT IT SHOWS. Always the newest event and only that one: news arriving when the board cuts
 * here on its own (see PMSingaporePage), or the last thing that landed if somebody opens the page
 * by hand. Every announcement carries the IST timestamp of when it actually happened, so the page
 * can never imply that old news is new. A message log was tried beside it and removed — it turned
 * an announcement back into a dashboard of activity.
 *
 * TELLING THE THREE KINDS APART. Retention and the two upsells must be distinguishable across a
 * room, before a word is read, so the kind is carried four times over: the colour, a pattern
 * inside the emblem (solid / diagonal / dashes, which survives a colour-blind viewer and a badly
 * calibrated television where colour alone does not), the broadcast phrase, and the words spelled
 * out in full. ONE-TIME UPSELL beside RECURRING UPSELL is exactly the pair a glance conflates.
 *
 * [PM-SG GIF 2026-10-07, user] A GIF PLAYS BEHIND THE CARD. The user chose look 1, "Dimmed", from
 * a live template: one of their GIFs fills the whole screen for as long as the card is up, darkened
 * so the figures in front stay readable across the room. Which GIF is a shuffled deck — every one
 * once before any repeats — kept in notifierGifs.ts. The card itself, its sizes and its sound are
 * unchanged. The halo and confetti now show only when there is no GIF (a file that fails to load
 * falls back to exactly the old card).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { RED, TEXT, EASE, prefersReducedMotion } from './chrome';
import { AnnouncementPayload, KIND_COLORS, KIND_HEADS, fmtNum, hexA } from './raceModel';
import { armAudio, playVictory, wasBlocked } from './sound';
import { gifForAnnouncement, gifUrl, warmNextGif } from './notifierGifs';

const KIND_LABELS: Record<string, string> = {
  Retention: 'RETENTION',
  'One-Time Upsell': 'ONE-TIME UPSELL',
  'Recurring Upsell': 'RECURRING UPSELL',
};

/**
 * [2026-08-12] THE ANNOUNCEMENT USES MATTE TONES, NOT THE FEED'S SIGNAL COLOURS.
 *
 * KIND_COLORS are chosen to be legible as small marks — a dot beside a name, a chip in a list —
 * so they are fully saturated. Spread across a 65" panel as a headline, a figure the height of a
 * hand and a halo behind both, that same green stops reading as a livery and starts reading as a
 * highlighter: it is the brightest thing on any page of this board by a distance.
 *
 * These are the same three hues pulled back into the family the rest of the board is painted in —
 * the liveries in raceModel and the podium metals are all deliberately matte, for exactly this
 * reason. The kind is still unmistakable, because it was never carried by saturation alone: the
 * pattern, the phrase and the spelled-out words all still say it.
 */
const KIND_TONES: Record<string, string> = {
  Retention: '#5EB98B',
  'One-Time Upsell': '#5AA3C6',
  'Recurring Upsell': '#4FB2AD',
};

/** Pattern, not just hue — see the note at the top of the file. */
const KIND_PATTERNS: Record<string, string> = {
  Retention: 'radial-gradient(circle at 50% 50%, var(--k) 0 42%, transparent 42%)',
  'One-Time Upsell': 'repeating-linear-gradient(115deg, var(--k) 0 5px, transparent 5px 11px)',
  'Recurring Upsell': 'repeating-linear-gradient(0deg, var(--k) 0 4px, transparent 4px 10px)',
};

/* ═════════════════════════════════════════════════════════════ motion ═════ */

const slamIn = keyframes`
  from { opacity: 0; transform: scale(1.35); filter: blur(14px); }
`;
const riseIn = keyframes`
  from { opacity: 0; transform: translateY(22px); filter: blur(7px); }
`;
const haloBreathe = keyframes`
  0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
  50%      { opacity: 0.85; transform: translate(-50%, -50%) scale(1.06); }
`;
const fall = keyframes`
  0%   { transform: translate3d(0, -129.6px, 0) rotate(0deg); opacity: 0; }
  8%   { opacity: 0.5; }
  90%  { opacity: 0.28; }
  100% { transform: translate3d(var(--dx), 1123.2px, 0) rotate(820deg); opacity: 0; }
`;
/* [PM-SG GIF 2026-10-07] The GIF arrives first and settles as it fades up; the card follows. */
const gifIn = keyframes`
  from { opacity: 0; transform: scale(1.12); }
`;

/* ═════════════════════════════════════════════════════════════ layout ═════ */

const View = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/* No kerb rails on this page. Everywhere else they frame a TRACK, which is what they are for;
   here they framed an announcement and read as two red-and-white barricades round a message. */

/** Confetti and the halo live here, behind the words and clipped to the page. */
const Sky = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;

  .halo {
    position: absolute;
    left: 50%; top: 50%;
    width: 950.4px; height: 950.4px;
    transform: translate(-50%, -50%);
    background: radial-gradient(circle at 50% 50%, var(--k-soft), transparent 66%);
    animation: ${haloBreathe} 7s ease-in-out infinite;
    will-change: transform, opacity;
  }
  .bit {
    position: absolute;
    top: 0;
    width: 5.76px;
    height: 11.14px;
    border-radius: 1px;
    opacity: 0;
    animation: ${fall} var(--t) linear infinite;
    will-change: transform, opacity;
    animation-delay: var(--del);
  }

  @media (prefers-reduced-motion: reduce) {
    .halo { animation: none; }
    .bit { display: none; }
  }
`;

/**
 * [PM-SG GIF 2026-10-07, user] THE GIF, full screen behind the card — look 1, "Dimmed".
 *
 * EDGE TO EDGE. This is rendered beside View, not inside it, so its containing block is the
 * board itself (absolutely positioned in PMSingaporePage) and `inset: 0` covers the board's
 * padding too. Inside View it would stop short of the edges by that padding and read as a
 * picture in a frame.
 *
 * `z-index: -1` is relative to the board's own stacking context: above the board's ink, below
 * everything else in it — the card, the CONNECTION LOST banner (which has no z-index and would
 * otherwise sit underneath) and the vignette, which darkens the GIF's edges as it does every page.
 *
 * The scrim is darkest where the figures are, so white type at ten feet never depends on which
 * frame of which GIF happens to be behind it. Same values as the template the user picked.
 *
 * ⚠ The clips are 300-600px wide, so at full screen they are scaled 3-6x and look soft. That was
 * shown in the template and accepted; `object-fit: cover` crops the wider ones rather than leave
 * bars.
 */
const Backdrop = styled.div<{ $ready: boolean; $run: boolean }>`
  position: absolute;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* Hidden until decoded, so a slow file fades up when it is ready instead of painting in
       strips over the card. */
    opacity: ${(p) => (p.$ready ? 1 : 0)};
  }
  .scrim {
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 62% 58% at 50% 50%,
      rgba(12, 12, 18, 0.68), rgba(12, 12, 18, 0.5) 72%, rgba(12, 12, 18, 0.6));
  }

  ${(p) => p.$ready && p.$run && css`
    img { animation: ${gifIn} 900ms ${EASE} backwards; }
  `}
`;

/** The announcement itself: one column, centred, read top to bottom. */
const Stage = styled.div<{ $run: boolean }>`
  position: relative;
  z-index: 2;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7.56px;
  padding: 21.6px 57.6px;
  text-align: center;

  /* The emblem. A ring in the kind's colour filled with the kind's pattern — the first thing the
     eye reaches, and already an answer to "what sort of news is this". */
  .emblem {
    width: 84.48px;
    height: 84.48px;
    border-radius: 50%;
    border: 4.22px solid var(--k);
    background: var(--k-pattern);
    box-shadow: 0 0 26px var(--k-glow), inset 0 0 26px rgba(0, 0, 0, 0.55);
    margin-bottom: 7.56px;
  }

  .head {
    font-size: 60px;
    font-weight: 900;
    letter-spacing: 0.04em;
    line-height: 1;
    color: var(--k);
    /* [2026-09-18, user] NO GLOW ON TEXT. A shadow behind type thickens every stroke and fills
    its counters, which is the opposite of legible at ten feet. Glow stays on the CARS and
    the lamps - objects, where it reads as light - and comes off anything anybody has to read. */
    text-shadow: none;
  }
  .kindline {
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 0.42em;
    text-indent: 0.42em;
    color: rgba(255, 255, 255, 0.75);
  }
  .who {
    margin-top: 8.64px;
    font-size: 79px;
    font-weight: 900;
    letter-spacing: 0.01em;
    line-height: 1.02;
    color: ${TEXT};
    overflow-wrap: anywhere;
  }
  /* The one figure the page exists to shout. */
  .amt {
    font-size: 127px;
    font-weight: 900;
    line-height: 0.98;
    letter-spacing: -0.03em;
    font-variant-numeric: tabular-nums;
    color: var(--k);
    text-shadow: none;
  }
  .move {
    margin-top: 6.48px;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: 0.16em;
    color: var(--k);
    opacity: 0.9;
    font-variant-numeric: tabular-nums;
  }
  .move b { color: ${TEXT}; font-weight: 900; }
  .when {
    margin-top: 4.32px;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.24em;
    color: rgba(255, 255, 255, 0.75);
  }

  /* [PM-SG GIF 2026-10-07] --d0 holds the whole sequence back while a GIF lands first (set on
     View; 0ms when there is no GIF, which is exactly the old timing). */
  ${(p) => p.$run && css`
    .emblem   { animation: ${riseIn} 520ms ${EASE} backwards; animation-delay: var(--d0, 0ms); }
    .head     { animation: ${slamIn} 620ms ${EASE} backwards; animation-delay: calc(var(--d0, 0ms) + 120ms); }
    .kindline { animation: ${riseIn} 460ms ${EASE} backwards; animation-delay: calc(var(--d0, 0ms) + 300ms); }
    .who      { animation: ${riseIn} 520ms ${EASE} backwards; animation-delay: calc(var(--d0, 0ms) + 380ms); }
    .amt      { animation: ${slamIn} 640ms ${EASE} backwards; animation-delay: calc(var(--d0, 0ms) + 520ms); }
    .move     { animation: ${riseIn} 460ms ${EASE} backwards; animation-delay: calc(var(--d0, 0ms) + 720ms); }
    .when     { animation: ${riseIn} 460ms ${EASE} backwards; animation-delay: calc(var(--d0, 0ms) + 800ms); }
  `}
`;

/**
 * Shown only when a play was actually attempted and the browser refused for want of a gesture.
 * Silence with no explanation is indistinguishable from "the sound was never built", and this is
 * a screen nobody stands in front of — so it says what is wrong and how to fix it in four words.
 * The real fix is the kiosk autoplay flag; this is what covers the day somebody forgets it.
 */
const TapForSound = styled.button`
  position: absolute;
  right: 23.04px;
  bottom: 12.96px;
  z-index: 4;
  padding: 0.5em 0.9em;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(10, 10, 14, 0.7);
  color: rgba(255, 255, 255, 0.75);
  font: inherit;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.2em;
  cursor: pointer;

  &:hover { color: ${TEXT}; border-color: rgba(255, 255, 255, 0.3); }
`;

const Empty = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8.64px;
  text-align: center;

  .e1 { font-size: 38px; font-weight: 900; letter-spacing: 0.2em; color: ${TEXT}; }
  .e2 {
    font-size: 20px; font-weight: 700; letter-spacing: 0.2em;
    color: rgba(255, 255, 255, 0.75);
  }
`;

/* ═══════════════════════════════════════════════════════════════ helpers ═══ */

/** Announcements are stamped in UTC; the board thinks in IST, so that is what is shown. */
const IST = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kolkata', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
});
function stamp(iso: string): string {
  const parts = IST.formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)?.value || '';
  return `${get('day')} · ${get('hour')}:${get('minute')}`;
}

/* Deterministic, never Math.random(): a random scatter would re-throw every piece on each
   thirty-second poll and the whole layer would twitch as the figures refreshed. */
const CONFETTI = Array.from({ length: 22 }, (_, i) => ({
  left: (i * 3.37 + 2) % 97,
  dx: `${((i % 7) - 3) * 1.9}vw`,
  t: `${6.5 + (i % 8) * 1.15}s`,
  del: `${-(i * 0.61).toFixed(2)}s`,
  tone: i % 4,
}));

/* ═══════════════════════════════════════════════════════════════ the view ══ */

interface Props {
  feed: AnnouncementPayload | null;
  month: string;
  active: boolean;
}

const NotifierView: React.FC<Props> = ({ feed, month, active }) => {
  const feat = feed?.events?.[0] || null;
  const [run, setRun] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);

  /* [PM-SG GIF 2026-10-07] One GIF per announcement, dealt off the shuffled deck. Keyed on the
     id, so reopening the page on the same card shows the same GIF and does not use up the next. */
  const featId = feat ? feat.id : null;
  const gif = useMemo(() => (featId === null ? null : gifForAnnouncement(featId)), [featId]);
  /* Whether THIS gif loaded (true) or failed (false); no answer yet means still loading. Kept with
     the slug it belongs to, so a new GIF never inherits the previous one's answer. */
  const [gifLoad, setGifLoad] = useState<{ slug: string; ok: boolean } | null>(null);
  const gifOk = gif !== null && gifLoad !== null && gifLoad.slug === gif ? gifLoad.ok : null;
  /* A file that fails to load gives back exactly the old card: no backdrop, halo and confetti. */
  const showGif = gif !== null && gifOk !== false;
  const gifRef = useRef<HTMLImageElement>(null);

  /* A file already in the cache can finish loading before React's handler sees it; ask it directly. */
  useEffect(() => {
    const img = gifRef.current;
    if (gif !== null && img && img.complete && img.naturalWidth > 0) {
      setGifLoad((prev) => (prev && prev.slug === gif && prev.ok ? prev : { slug: gif, ok: true }));
    }
  }, [gif]);

  /* Once this card's GIF is up, fetch the one the next card will get. */
  useEffect(() => { if (gifOk) warmNextGif(); }, [gifOk]);

  /* Replay when the announcement changes, and whenever the page is opened. */
  useEffect(() => {
    if (prefersReducedMotion()) return;
    setRun(false);
    const t = window.setTimeout(() => setRun(true), 20);
    return () => window.clearTimeout(t);
  }, [feat?.id, active]);

  /* Arm the gesture unlock and warm the buffer as soon as the page exists, so the first
     announcement plays on the frame it arrives rather than after a fetch. */
  useEffect(() => armAudio(), []);

  /**
   * THE SOUND. Fires when this page TAKES THE SCREEN — which covers both cases the user asked
   * for: the board cutting here because something landed, and somebody opening it by hand.
   *
   * Keyed on the announcement's id as well as `active`, so a new gain arriving while the page is
   * already up plays again. It cannot double-fire on a poll: the id is what changes, and a poll
   * that brings no new announcement leaves it identical.
   */
  useEffect(() => {
    if (!active || !feat) return;
    let alive = true;
    void playVictory().then(() => { if (alive) setNeedsTap(wasBlocked()); });
    return () => { alive = false; };
  }, [active, feat?.id]);

  const k = feat ? (KIND_TONES[feat.kind] || KIND_COLORS[feat.kind] || RED) : RED;
  /* Halo and glow pulled well down with the hue: a wash at 13% behind a figure this size is a
     lit screen, not a highlight. */
  const vars = {
    '--k': k,
    '--k-soft': hexA(k, 0.07),
    '--k-glow': hexA(k, 0.17),
    '--k-pattern': feat ? (KIND_PATTERNS[feat.kind] || 'transparent') : 'transparent',
    /* [PM-SG GIF 2026-10-07] the card waits for the GIF to land first; see Stage */
    '--d0': showGif ? '380ms' : '0ms',
  } as React.CSSProperties;

  return (
    <>
      {showGif && gif !== null && (
        <Backdrop aria-hidden="true" $ready={gifOk === true} $run={run}>
          <img
            ref={gifRef}
            src={gifUrl(gif)}
            alt=""
            decoding="async"
            draggable={false}
            onLoad={() => setGifLoad({ slug: gif, ok: true })}
            onError={() => setGifLoad({ slug: gif, ok: false })}
          />
          <div className="scrim" />
        </Backdrop>
      )}
      <View style={vars}>
        {/* The halo and confetti were this page's celebration; with a GIF behind the card the
            GIF is, and both would only add noise over it. */}
        {!showGif && (
          <Sky aria-hidden="true">
            <div className="halo" />
            {feat && CONFETTI.map((c, i) => (
              <i
                key={i}
                className="bit"
                style={{
                  left: `${c.left}%`,
                  /* The kind's own colour dominates; white and a cool grey keep it from reading as
                     a single-hue wash. */
                  background: c.tone === 1 ? '#B9BFCC' : c.tone === 3 ? '#7C8493' : k,
                  '--dx': c.dx,
                  '--t': c.t,
                  '--del': c.del,
                } as React.CSSProperties}
              />
            ))}
          </Sky>
        )}

        {feat ? (
          <Stage $run={run}>
            <div className="emblem" />
            <div className="head">{KIND_HEADS[feat.kind] || 'GAIN'}!</div>
            <div className="kindline">{KIND_LABELS[feat.kind] || feat.kind.toUpperCase()}</div>
            <div className="who">{feat.pmName.toUpperCase()}</div>
            {/* The INCREASE is the news — 1,000 becoming 1,500 announces 500 — and the standing
                total goes underneath as context rather than as the headline. */}
            <div className="amt">+{fmtNum(feat.increase, 0)}</div>
            <div className="move">
              {KIND_LABELS[feat.kind] || feat.kind.toUpperCase()} {fmtNum(feat.previous, 0)}
              {' → '}<b>{fmtNum(feat.newValue, 0)}</b> FOR {month}
            </div>
            <div className="when">{stamp(feat.createdAt)} IST</div>
          </Stage>
        ) : (
          <Empty>
            <div className="e1">STANDING BY</div>
            <div className="e2">NO RETENTION OR UPSELL ACTIVITY IN {month}</div>
          </Empty>
        )}

        {needsTap && (
          <TapForSound
            type="button"
            onClick={() => { void playVictory().then(() => setNeedsTap(wasBlocked())); }}
          >
            🔇 TAP FOR SOUND
          </TapForSound>
        )}
      </View>
    </>
  );
};

/**
 * [PM-SG SMOOTH 2026-08-13] Memoised. The board shell re-renders on its own state — the page
 * rotation tick, the sweep, an announcement, the diagnostic toggle, a resize — none of which
 * change this view's props. Without this, each of those re-rendered the whole view mid-animation.
 */
export default React.memo(NotifierView);
